// scerv.com/bar/<restaurantId>: what the bar's QR card opens in a phone's
// camera. A restaurant on the V2 engine (in a build with a gateway) gets the
// bar tab page right here (engine/EngineBarTab.js). Anywhere else this page
// sends the guest to the app: get it, or open it and scan the card. Scanning
// the card inside the app opens the restaurant's bar tab directly.
import React, { Suspense, lazy, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import { chooseBarPage } from "../engine/barChoice";
import { engineGatewayUrl } from "../engine/engineConfig";
import { APP_LINKS } from "../engine/guestLogic";

// Loaded only for engine restaurants, so Stripe's script stays off this page otherwise.
const EngineBarTab = lazy(() => import("../engine/EngineBarTab"));

// The restaurant's public profile (restaurantPublic/<id>): its name and features.
const readRestaurant = async (id) => {
	const snap = await getDoc(doc(db, "restaurantPublic", id));
	return snap.exists() ? snap.data() : null;
};

const Page = styled.main`
	align-items: center;
	background: #ffffff;
	display: flex;
	justify-content: center;
	min-height: 100vh;
	padding: 24px 16px;
`;

const Card = styled.section`
	max-width: 420px;
	text-align: center;
	width: 100%;
`;

const Title = styled.h1`
	color: #0b3b45;
	font-size: 1.6rem;
	margin: 0 0 12px;
`;

const Body = styled.p`
	color: #33484d;
	font-size: 1rem;
	line-height: 1.5;
	margin: 0 0 16px;
`;

const Button = styled.a`
	background: #006d77;
	border-radius: 10px;
	color: #ffffff;
	display: block;
	font-weight: 600;
	margin: 0 0 10px;
	padding: 14px 16px;
	text-decoration: none;
`;

function AppLanding() {
	return (
		<Page>
			<Card>
				<Title>Start your bar tab</Title>
				<Body>
					Bar tabs run in the Scerv app. Get the app, sign in with your phone number, then tap Scan and scan this code again
					to open your tab. You can order, see your total, and close it from your phone.
				</Body>
				<Button href={APP_LINKS.ios}>Get Scerv for iPhone</Button>
				<Button href={APP_LINKS.android}>Get Scerv for Android</Button>
				<Body>Already have Scerv? Open it, tap Scan, and scan the code at the bar.</Body>
			</Card>
		</Page>
	);
}

export default function BarTabLanding() {
	const { restaurantId } = useParams();
	const [choice, setChoice] = useState(() => (engineGatewayUrl() ? null : { page: "app" }));

	useEffect(() => {
		if (!engineGatewayUrl()) return undefined;
		let live = true;
		setChoice(null);
		chooseBarPage(restaurantId, { gatewayUrl: engineGatewayUrl(), read: readRestaurant }).then((next) => live && setChoice(next));
		return () => {
			live = false;
		};
	}, [restaurantId]);

	const finding = (
		<Page>
			<Card>
				<Body aria-live="polite">Finding the bar...</Body>
			</Card>
		</Page>
	);
	if (!choice) return finding;
	if (choice.page === "engine") {
		return (
			<Suspense fallback={finding}>
				<EngineBarTab key={choice.restaurant.id} gatewayUrl={choice.gatewayUrl} restaurant={choice.restaurant} />
			</Suspense>
		);
	}
	return <AppLanding />;
}
